import { useState, useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { FormControl, FormItem, FormLabel, FormMessage } from '@/common/ui/form'

interface Game {
	id: string
	name: string
	displayName?: string
}

interface GameSelectorProps {
	field: {
		value?: string | null
		onChange: (value: string) => void
	}
	games: Game[]
	label?: string
	placeholder?: string
	required?: boolean
}

// Allowed games list
const ALLOWED_GAME_NAMES = [
	'Crypto Gold',
	'Wild Bandito',
	'Buffalo Win',
	'Jurassic Kingdom',
	'Supermarket Spree',
	'Spirited Wonders',
	'Rooster Rumble',
	'Battleground Royale',
	'Butterfly Blossom',
	'Speed Winner',
	'Wild Bounty Showdown',
	'Alchemy Gold',
	'Diner Delights',
	'Hawaiian Tiki',
	'Fruity Candy',
	'Midas Fortune',
	'Fortune Rabbit',
	'Mafia Mayhem',
	'Lucky Clover Lady',
	'Gemstones Gold',
	"Geisha's Revenge",
	'Yakuza Honor',
	'Kraken Gold Rush',
	'Fortune Tree',
	'Dragon Legend',
	'Mr. Hallow-Win',
	'Double Fortune',
	'Journey to the Wealth',
	"Captain's Bounty",
	'Flirting Scholar',
	'Muay Thai Champion',
	'Mahjong Ways',
	'Bikini Paradise',
	'Caishen Wins',
	'Mahjong Ways 2',
	'Wild Fireworks',
	'Treasures of Aztec',
	'Bali Vacation',
	'Fortune Ox',
]

export default function GameSelector({
	field,
	games,
	label = 'เกม (PGHARD เท่านั้น)',
	placeholder = 'เลือกเกม',
	required = false,
}: GameSelectorProps) {
	const [gameSearch, setGameSearch] = useState('')

	// Filter games by allowed list and search query
	const filteredGames = useMemo(() => {
		// First filter by allowed games
		const allowedGames = games.filter((game) =>
			ALLOWED_GAME_NAMES.includes(game.name),
		)

		// Then filter by search query
		if (!gameSearch) return allowedGames
		return allowedGames.filter((game) => {
			const displayName = game.displayName || game.name
			return displayName.toLowerCase().includes(gameSearch.toLowerCase())
		})
	}, [games, gameSearch])

	const selectedGame = useMemo(
		() => games.find((game) => game.id === field.value),
		[games, field.value],
	)

	return (
		<FormItem>
			<FormLabel required={required}>{label}</FormLabel>
			<FormControl>
				<Popover
					onOpenChange={(open) => {
						if (!open) {
							setGameSearch('')
						}
					}}
				>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							role="combobox"
							className="w-full justify-between"
						>
							<span className="truncate">
								{selectedGame
									? selectedGame.displayName || selectedGame.name
									: placeholder}
							</span>
							<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-full p-0" align="start">
						<div className="space-y-2 p-2">
							<Input
								placeholder="ค้นหาเกม..."
								className="h-8 text-sm"
								value={gameSearch}
								onChange={(e) => setGameSearch(e.target.value)}
							/>
							<div className="max-h-[300px] space-y-1 overflow-y-auto">
								{filteredGames.length > 0 ? (
									filteredGames.map((game) => (
										<div
											key={game.id}
											className={`flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent ${
												field.value === game.id ? 'bg-accent' : ''
											}`}
											onClick={() => {
												field.onChange(game.id)
											}}
										>
											<span className="truncate">
												{game.displayName || game.name}
											</span>
										</div>
									))
								) : (
									<div className="py-2 text-center text-xs text-muted-foreground">
										ไม่พบเกม
									</div>
								)}
							</div>
						</div>
					</PopoverContent>
				</Popover>
			</FormControl>
			<FormMessage />
		</FormItem>
	)
}
